import { useMemo, useState } from "react";
import PhoneInput, { getCountryCallingCode } from "react-phone-number-input";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import flags from "react-phone-number-input/flags";
import en from "react-phone-number-input/locale/en.json";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { devWarn } from "@/lib/devLogger";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import "react-phone-number-input/style.css";

// Custom country selector matching react-phone-number-input's expected
// `{ value, onChange, options, disabled }` interface (see the library's
// own CountrySelect.js), but rendered as a searchable Shadcn
// Popover+Command dropdown -- with flags -- to match the site's premium
// dark theme rather than a bare native <select>.
const SearchableCountrySelect = ({ value, onChange, options, disabled }) => {
  const [open, setOpen] = useState(false);

  const countryOptions = useMemo(
    () =>
      options
        .filter((opt) => opt.value && !opt.divider)
        .map((opt) => ({
          code: opt.value,
          name: opt.label,
          dialCode: `+${getCountryCallingCode(opt.value)}`,
        })),
    [options]
  );

  const selected = countryOptions.find((c) => c.code === value);
  const Flag = value ? flags[value] : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className="phone-country-trigger neu-inset"
          data-testid="phone-country-select-trigger"
          aria-label="Select country"
        >
          {Flag ? <Flag title={selected?.name || ""} /> : <span className="phone-country-flag-placeholder" />}
          <ChevronDown size={14} className="opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="phone-country-popover p-0"
        data-testid="phone-country-select-popover"
      >
        <Command className="bg-transparent">
          <CommandInput placeholder="Search country or code…" className="text-sm" data-testid="phone-country-search-input" />
          <CommandList>
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandGroup>
              {countryOptions.map((c) => {
                const OptionFlag = flags[c.code];
                return (
                  <CommandItem
                    key={c.code}
                    value={`${c.name} ${c.dialCode} ${c.code}`}
                    onSelect={() => {
                      onChange(c.code);
                      setOpen(false);
                    }}
                    data-testid={`phone-country-option-${c.code.toLowerCase()}`}
                    className="phone-country-option"
                  >
                    {OptionFlag ? <OptionFlag title={c.name} /> : null}
                    <span className="flex-1 truncate">{c.name}</span>
                    <span className="opacity-60 text-xs">{c.dialCode}</span>
                    {c.code === value && <Check size={14} />}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

/**
 * International phone input -- searchable country selector (flag +
 * name + dial code), India (+91) default, E.164-formatted value,
 * styled to match the site's dark neumorphic inputs. Exposes the
 * resolved country code and dial code alongside the E.164 number via
 * onValueChange so callers can persist all three (per the Contact/
 * Appointment forms' storage requirements).
 */
const PhoneInputField = ({
  value,
  defaultCountry = "IN",
  onValueChange,
  disabled = false,
  testId = "phone-input",
  placeholder = "Phone number",
}) => {
  const handleChange = (nextValue) => {
    let country = null;
    let dialCode = null;
    if (nextValue) {
      try {
        // react-phone-number-input doesn't directly expose the resolved
        // country on change, so we derive it via libphonenumber-js
        // through the same metadata the input already uses.
        const parsed = parsePhoneNumberFromString(nextValue);
        if (parsed) {
          country = parsed.country || null;
          dialCode = parsed.countryCallingCode ? `+${parsed.countryCallingCode}` : null;
        }
      } catch (err) {
        // Expected while the user is mid-way through typing an
        // incomplete number -- value is still passed through as-is.
        // Logged only for dev visibility, never surfaced to the user.
        devWarn("Phone number could not be parsed yet:", err);
      }
    }
    onValueChange?.({ value: nextValue, country, dialCode });
  };

  // Memoized so PhoneInput doesn't receive a brand-new object identity
  // on every render (which would defeat any memoization it does
  // internally on its number-input element).
  const numberInputProps = useMemo(
    () => ({ className: cn("phone-number-input neu-inset"), "data-testid": `${testId}-number` }),
    [testId]
  );

  return (
    <div className="phone-input-wrap" data-testid={testId}>
      <PhoneInput
        international
        defaultCountry={defaultCountry}
        flags={flags}
        labels={en}
        value={value}
        onChange={handleChange}
        disabled={disabled}
        placeholder={placeholder}
        countrySelectComponent={SearchableCountrySelect}
        numberInputProps={numberInputProps}
      />
    </div>
  );
};

export default PhoneInputField;
